<template>
  <label class="card">
    <input
      class="card__input"
      type="checkbox"
      :checked="checked"
      :disabled="disabled"
      @change="$emit('change', $event.target.checked)"
    />
    <div class="card__body" :class="{ 'card__body--checked': checked }">
      <div class="card__body-cover">
        <div v-if="emptyImage" class="text-image">
          <p>{{ imageText }}</p>
        </div>
        <img v-else class="card__body-cover-image" :src="img" :alt="title">
        <span class="card__body-cover-checkbox">
          <svg class="card__body-cover-checkbox--svg" viewBox="0 0 12 10">
            <polyline points="1.5 6 4.5 9 10.5 1"></polyline>
          </svg>
        </span>
      </div>
      <header class="card__body-header">
        <h2 class="card__body-header-title">{{ title }}</h2>
        <p class="card__body-header-subtitle">{{ description }}</p>
      </header>
    </div>
  </label>
</template>

<script>
import missingImage from '../../../../../public/images/original/missing.png'

export default {
  name: 'CheckboxCard',
  props: {
    imageSrc: String,
    title: String,
    description: String,
    checked: Boolean,
    disabled: {
      type: Boolean,
      default: false
    },
    active: Boolean,
    emptyImage: {
      type: Boolean,
      default: false
    },
    imageText: {
      type: String,
      default: 'No image'
    }
  },
  data () {
    return {
      missingImg: missingImage
    }
  },
  computed: {
    img () {
      return this.imageSrc ? this.imageSrc : this.missingImg
    }
  }
}
</script>

<style scoped lang="scss">
.card {
  --background: #fff;
  --background-checkbox: #38948F;
  --background-image: #fff, #{rgba(#006baf, 0.2)};
  --text-color: #666;
  --text-headline: #000;
  --card-shadow: #38948F;
  --card-height: 240px;
  --card-width: 220px;
  --card-radius: 12px;
  --header-height: 47px;
  --blend-mode: overlay;
  --transition: 0.15s;

  &__input {
    position: absolute;
    display: block;
    outline: none;
    border: none;
    background: none;
    padding: 0;
    margin: 0;
    appearance: none;

    &:disabled {
      & ~ .card__body {
        cursor: not-allowed;
        opacity: 0.5;
        &:active {
          --scale: 1;
        }
      }
    }
  }

  &__body {
    display: grid;
    grid-auto-rows: calc(var(--card-height) - var(--header-height)) auto;
    background: var(--background);
    height: var(--card-height);
    width: var(--card-width);
    border-radius: var(--card-radius);
    overflow: hidden;
    position: relative;
    cursor: pointer;
    box-shadow: var(--shadow, 0 4px 4px 0 rgba(0, 0, 0, 0.02));
    transition: transform var(--transition), box-shadow var(--transition);
    transform: scale(var(--scale, 1)) translateZ(0);

    &:active {
      --scale: 0.96;
    }

    &--checked {
      --shadow: 0 0 0 3px var(--card-shadow);

      .card__body-cover {
        &:after {
          --opacity-bg: 0;
        }

        &-image {
          --filter-bg: grayscale(0);
        }

        &-checkbox {
          --check-bg: var(--background-checkbox);
          --check-border: #fff;
          --check-scale: 1;
          --check-opacity: 1;

          &--svg {
            --stroke-color: #fff;
            --stroke-dashoffset: 0;
          }
        }
      }
    }

    &-cover {
      --c-border: var(--card-radius) var(--card-radius) 0 0;
      --c-width: 100%;
      --c-height: 100%;
      position: relative;
      overflow: hidden;
      display: flex;
      justify-content: center;
      &:after {
        content: "";
        position: absolute;
        left: 0;
        top: 0;
        width: var(--c-width);
        height: var(--c-height);
        border-radius: var(--c-border);
        background: linear-gradient(to bottom right, var(--background-image));
        mix-blend-mode: var(--blend-mode);
        opacity: var(--opacity-bg, 1);
        transition: opacity var(--transition) linear;
      }

      &-image {
        // width: var(--c-width);
        height: var(--c-height);
        object-fit: cover;
        border-radius: var(--c-border);
        filter: var(--filter-bg, grayscale(1));
        // clip-path: polygon(
        //   0% 0%,
        //   100% 0%,
        //   var(--x-y1, 100% 90%),
        //   var(--x-y2, 67% 83%),
        //   var(--x-y3, 33% 90%),
        //   var(--x-y4, 0% 85%)
        // );
      }

      &-checkbox {
        background: var(--check-bg, var(--background-checkbox));
        border: 2px solid var(--check-border, #fff);
        position: absolute;
        right: 10px;
        top: 10px;
        z-index: 1;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        opacity: var(--check-opacity, 0);
        transition: transform var(--transition),
          opacity calc(var(--transition) * 1.2) linear,
          -webkit-transform var(--transition) ease;
        transform: scale(var(--check-scale, 0));

        &--svg {
          width: 13px;
          height: 11px;
          display: inline-block;
          vertical-align: top;
          fill: none;
          margin: 7px 0 0 5px;
          stroke: var(--stroke-color, #fff);
          stroke-width: 2;
          stroke-linecap: round;
          stroke-linejoin: round;
          stroke-dasharray: 16px;
          stroke-dashoffset: var(--stroke-dashoffset, 16px);
          transition: stroke-dashoffset 0.4s ease var(--transition);
        }
      }
    }

    &-header {
      height: var(--header-height);
      background: var(--background);
      padding: 0 10px 10px 10px;

      &-title {
        color: var(--text-headline);
        font-weight: 500;
        font-size: 1.19rem;
        line-height: 1;
        margin-bottom: 0px;
      }

      &-subtitle {
        color: var(--text-color);
        margin-bottom: 0px;
        font-weight: 500;
        font-size: 13px;
      }
    }
  }
}

.text-image {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  text-align: center;
  p {
    font-family: 'Lato', sans-serif;
    font-size: 2rem;
    font-weight: 600;
    color: #aaa;
    margin-bottom: 0;
    line-height: 30px;
  }
}


</style>